import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Eye, Loader2 } from "lucide-react";
import { PropagationStatusBadge } from "@/components/propagation/propagation-status-badge";
import { cn } from "@/lib/utils";
import type { IStoreComparison } from "src/@types/campaign-comparison";
import { ComparisonStatusBadge } from "./comparison-status-badge";
import { ScheduledJobBadge } from "./scheduled-job-badge";

export type ComparisonRow = IStoreComparison & { __loading?: boolean };

function PendingCell() {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
      <Loader2 className="h-3 w-3 animate-spin" />
      Comparando…
    </span>
  );
}

const formatDateTime = (value: string | null): string =>
  value
    ? new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

interface BuildColumnsArgs {
  onDetail: (store: ComparisonRow) => void;
  hasItemComparison: boolean;
}

export function buildColumns({
  onDetail,
  hasItemComparison,
}: BuildColumnsArgs): ColumnDef<ComparisonRow>[] {
  const naCell = <span className="text-muted-foreground">—</span>;
  const untracked = (row: ComparisonRow) => row.propagationTracked === false;
  const columns: ColumnDef<ComparisonRow>[] = [
    {
      accessorKey: "storeCode",
      header: "Loja",
      cell: ({ row }) => {
        const store = row.original;
        return (
          <span
            className={cn(
              "font-medium",
              store.__loading && "text-muted-foreground"
            )}
          >
            {store.storeCode}
            {store.storeName ? ` - ${store.storeName}` : ""}
          </span>
        );
      },
    },
    {
      accessorKey: "comparisonStatus",
      header: "Comparação",
      cell: ({ row }) =>
        row.original.__loading ? (
          <PendingCell />
        ) : (
          <ComparisonStatusBadge status={row.original.comparisonStatus} />
        ),
    },
    {
      id: "propagation",
      header: "Propagação",
      cell: ({ row }) =>
        row.original.__loading || untracked(row.original) ? (
          naCell
        ) : (
          <PropagationStatusBadge status={row.original.propagation.status} />
        ),
    },
  ];

  if (hasItemComparison) {
    columns.push({
      id: "items",
      header: "Itens (Retaguarda vs Loja)",
      cell: ({ row }) => {
        if (row.original.__loading)
          return <span className="text-muted-foreground">-</span>;
        const item = row.original.itemComparison;
        if (!item) return <span className="text-muted-foreground">-</span>;
        return (
          <span
            className={cn(
              "font-mono text-sm",
              !item.inSync &&
                "font-semibold text-yellow-700 dark:text-yellow-400"
            )}
          >
            {item.varCount} vs {item.storeCount}
          </span>
        );
      },
    });
  }

  columns.push(
    {
      id: "lastAttempt",
      header: "Última Tentativa de Sync",
      cell: ({ row }) => {
        if (row.original.__loading || untracked(row.original)) return naCell;
        const p = row.original.propagation;
        return (
          <span className="text-sm">
            {formatDateTime(p.lastAttemptAt || p.lastAttempt)}
          </span>
        );
      },
    },
    {
      id: "scheduledJob",
      header: "Sincronização",
      cell: ({ row }) => {
        if (row.original.__loading || untracked(row.original)) return naCell;
        const job = row.original.scheduledJob;
        return job ? (
          <ScheduledJobBadge job={job} />
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      id: "actions",
      header: "Detalhe",
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDetail(row.original)}
        >
          <Eye className="mr-1 h-3 w-3" />
          Detalhe
        </Button>
      ),
    }
  );

  return columns;
}
